import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { GlobalService } from '../../../global/services/global.service';

/**
 * Replicates AngularJS GridFactory.getDataCustom for Rules Engine (validation rules grid)
 * and RuleEngineNewConfigFactory for rule CRUD / action / cache operations.
 *
 * Grid:          loadGridJson?action_code=d4yabw&grid_id=102
 * Action codes:  dszxm0 (save), yublv5 (update), zxs8ob (addAction),
 *                nrvl3o (delinkAction), g1pp14 (addCache), cl2s8u (delinkCache)
 *
 * The original AngularJS app globally overrides $httpProvider.defaults.transformRequest
 * to send ALL POST bodies as application/x-www-form-urlencoded (see app.js line 984-1026).
 * We replicate this by sending HttpParams as the body for every POST call.
 *
 * The legacy backend additionally requires the standard global context params
 * (tax_year, scenario, jcd_key, scenarioCode, is_issue_key_enabled, ...) on these
 * action calls. AuthInterceptor only enriches `saveJsonObject` URLs, so the
 * `loadJsonObject` action calls (dszxm0/zxs8ob/g1pp14/cl2s8u) would otherwise be
 * sent with an incomplete payload and the backend rejects them. We merge
 * globalParams into every action body here so all six operations carry them.
 */
@Injectable({ providedIn: 'root' })
export class RulesEngineService {
  private readonly ACTION_CODE = 'd4yabw';
  private readonly GRID_ID = 102;

  private readonly baseUrl = environment.base_url;
  private readonly commonApiUrl = (environment as any).batch_api_url || environment.base_url;

  private static readonly FORM_HEADERS = new HttpHeaders({
    'Content-Type': 'application/x-www-form-urlencoded;charset=utf-8',
  });

  constructor(
    private http: HttpClient,
    private globalService: GlobalService,
  ) {}

  /**
   * Build an x-www-form-urlencoded body from the action-specific params and
   * append the global context params (tax_year, scenario, jcd_key, etc.).
   * Action-specific params win over global ones with the same key.
   */
  private buildActionBody(params: Record<string, any>): HttpParams {
    let body = new HttpParams();
    const globalParams = this.globalService?.global?.globalParams || {};
    Object.keys(globalParams).forEach((key) => {
      const value = (globalParams as any)[key];
      if (value === null || value === undefined || params[key] !== undefined) {
        return;
      }
      body = body.set(
        key,
        typeof value === 'object' ? JSON.stringify(value) : String(value)
      );
    });
    Object.keys(params).forEach((key) => {
      const value = params[key];
      if (value === null || value === undefined) {
        return;
      }
      body = body.set(
        key,
        typeof value === 'object' ? JSON.stringify(value) : String(value)
      );
    });
    return body;
  }

  loadGridData(
    baseUrl: string,
    globalService: any,
    filterParams: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/loadGridJson?action_code=${this.ACTION_CODE}&grid_id=${this.GRID_ID}`;
    const globalParams = globalService?.globalParams ?? globalService ?? {};
    const params = { ...globalParams, ...filterParams };

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http.post<any>(url, params, {
      headers,
      withCredentials: true,
    });
  }

  saveNewRule(clientKey: any, ruleModel: any): Observable<any> {
    const url = `${this.baseUrl}/loadJsonObject?action_code=dszxm0`;
    const body = this.buildActionBody({
      url: 'dszxm0',
      client_key: String(clientKey),
      clob_data: JSON.stringify([{ ...ruleModel }]),
    });
    return this.http.post<any>(url, body.toString(), {
      headers: RulesEngineService.FORM_HEADERS,
      withCredentials: true,
    });
  }

  updateRule(clientKey: any, ruleModel: any): Observable<any> {
    const url = `${this.baseUrl}/saveJsonObject?action_code=yublv5`;
    const body = this.buildActionBody({
      url: 'yublv5',
      client_key: String(clientKey),
      clob_data: JSON.stringify([{ ...ruleModel }]),
    });
    return this.http.post<any>(url, body.toString(), {
      headers: RulesEngineService.FORM_HEADERS,
      withCredentials: true,
    });
  }

  getLinkedActions(clientKey: any, ruleKey: any): Observable<any> {
    const url = `${this.commonApiUrl}/getDroolsRulesConfigDataByClient`;
    const body = {
      client_key: String(clientKey),
      rule_key: String(ruleKey),
    };
    return this.http.post<any>(url, body, {
      withCredentials: true,
    });
  }

  addAction(clientKey: any, ruleKey: any, actionKey: any): Observable<any> {
    const url = `${this.baseUrl}/loadJsonObject?action_code=zxs8ob`;
    const body = this.buildActionBody({
      new_action_key: String(actionKey),
      action_key: String(actionKey),
      client_key: String(clientKey),
      validation_rule_key: String(ruleKey),
    });
    return this.http.post<any>(url, body.toString(), {
      headers: RulesEngineService.FORM_HEADERS,
      withCredentials: true,
    });
  }

  delinkAction(clientKey: any, ruleActionKey: any, linkOrDelink: number | string): Observable<any> {
    const url = `${this.baseUrl}/saveJsonObject?action_code=nrvl3o`;
    const body = this.buildActionBody({
      rule_action_key: String(ruleActionKey),
      client_key: String(clientKey),
      lnk_or_d_lnk: linkOrDelink,
    });
    return this.http.post<any>(url, body.toString(), {
      headers: RulesEngineService.FORM_HEADERS,
      withCredentials: true,
    });
  }

  addCacheObject(clientKey: any, ruleKey: any, cacheKey: any): Observable<any> {
    const url = `${this.baseUrl}/loadJsonObject?action_code=g1pp14`;
    const body = this.buildActionBody({
      new_cache_key: String(cacheKey),
      url: 'g1pp14',
      cache_key: String(cacheKey),
      client_key: String(clientKey),
      validation_rule_key: String(ruleKey),
    });
    return this.http.post<any>(url, body.toString(), {
      headers: RulesEngineService.FORM_HEADERS,
      withCredentials: true,
    });
  }

  delinkCacheObject(clientKey: any, ruleKey: any, droolsCacheKey: any): Observable<any> {
    const url = `${this.baseUrl}/loadJsonObject?action_code=cl2s8u`;
    const body = this.buildActionBody({
      drools_cache_key: String(droolsCacheKey),
      validation_rule_key: String(ruleKey),
      client_key: String(clientKey),
    });
    return this.http.post<any>(url, body.toString(), {
      headers: RulesEngineService.FORM_HEADERS,
      withCredentials: true,
    });
  }
}
